import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useHotel } from "../../contexts/HotelContext";
import { getProcessingErrorsSummary } from "../../services/grantsApi";
import MenuItem from "./MenuItem";
import {
  KFS_CALLS_HREF,
  KFS_ARTICLES_HREF,
  BUR_CALLS_HREF,
  BUR_ARTICLES_HREF,
} from "../../constants/legacyGrantsRoutes";
import {
  HotelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DashboardIcon,
  DocumentIcon,
  SettingsIcon,
  HelpIcon,
  ChartBarIcon,
  CloseIcon,
} from "../icons";

// Active calls = OPEN + ANNOUNCED (parent path matches first submenu item for isActive highlighting)
const ACTIVE_CALLS_PATH = "/grants/calls?status=OPEN&status=ANNOUNCED";

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggle }) => {
  const { hasModuleEnabled, user, getTenantDisplayInfo } = useHotel();
  const location = useLocation();

  const isActive = (path: string) => {
    const [pathWithoutQuery, queryString] = path.split("?");
    if (pathWithoutQuery === "/" && location.pathname === "/") return true;
    if (
      pathWithoutQuery === "/" ||
      !location.pathname.startsWith(pathWithoutQuery)
    )
      return false;
    // Scheme-scoped entries (legacy "KFS/BUR (archiwum)" now deep-link to
    // /grants/calls?scheme=… and /grants/articles?scheme=…) must only match when
    // the current location carries the SAME scheme — otherwise they would light
    // up on the unified /grants/calls list. Other params (e.g. the default
    // ?status=… on "Aktywne") keep the prior prefix-match behaviour.
    const requiredScheme = queryString
      ? new URLSearchParams(queryString).get("scheme")
      : null;
    if (requiredScheme) {
      return new URLSearchParams(location.search).get("scheme") === requiredScheme;
    }
    return true;
  };

  const hasOffersAccess = hasModuleEnabled("hotel:offers");
  const hasGrantsAccess = hasModuleEnabled("grants:schedule");
  const hasGrantsCallsAccess = hasModuleEnabled("grants:calls");
  const hasGrantsFormsAccess = hasModuleEnabled("grants:deminimis");
  const hasAdminKNRead = hasModuleEnabled("admin:kiedynabory:read");
  const hasAdminKNWrite = hasModuleEnabled("admin:kiedynabory:write");
  const hasAdminKNRozliczeniaPaulina = hasModuleEnabled("admin:kiedynabory:rozliczenia-paulina");
  const hasAdminKNRozliczeniaTenants = hasModuleEnabled("admin:kiedynabory:rozliczenia-tenants");
  // Legacy permission check for backward compatibility
  const hasLegacyAdminKN = hasModuleEnabled("admin:kiedynabory");

  // Processing errors summary for badge count (admin or moderator with write access)
  const isAdmin = user?.role === "admin";
  const { data: processingErrorsSummary } = useQuery({
    queryKey: ["processing-errors-summary"],
    queryFn: getProcessingErrorsSummary,
    enabled: isAdmin || hasAdminKNWrite,
    refetchInterval: 60000,
    staleTime: 30000,
  });

  // AC-1: "Nabory" section visible to admin, moderators with write, or customers with grants modules.
  // `admin:kiedynabory:read` alone is NOT enough — read-only browse mode is out of scope (spec L84).
  const hasNaboryAccess =
    isAdmin || hasAdminKNWrite || hasGrantsAccess || hasGrantsCallsAccess;

  const menuItems = [
    {
      id: "dashboard",
      label: "Panel główny",
      path: "/",
      icon: DashboardIcon,
      exact: true,
    },
    // Oferty - tylko jeśli ma uprawnienia do ofert
    ...(hasOffersAccess
      ? [
          {
            id: "offers",
            label: "Oferty",
            path: "/offers",
            icon: DocumentIcon,
            submenu: [
              { label: "Lista ofert", path: "/offers" },
              { label: "Nowa oferta", path: "/offers/create" },
            ],
          },
        ]
      : []),
    // Zarządzanie hotelem - sprawdzamy konkretne uprawnienia dla każdej sekcji
    ...(hasModuleEnabled("hotel:manage")
      ? [
          {
            id: "hotel-management",
            label: "Zarządzanie hotelem",
            path: "/hotel",
            icon: SettingsIcon,
            submenu: [
              { label: "Ustawienia hotelu", path: "/hotel/settings" },
              { label: "Składniki oferty", path: "/hotel/category-options" },
              { label: "Design oferty", path: "/hotel/design" },
              { label: "Zarządzanie plikami", path: "/hotel/files" },
            ],
          },
        ]
      : []),
    // Formularze de minimis - jeśli ma uprawnienia grants:deminimis
    ...(hasGrantsFormsAccess
      ? [
          {
            id: "grants-forms",
            label: "De minimis",
            path: "/grants/forms",
            icon: DocumentIcon,
            submenu: [
              { label: "Formularz", path: "/grants/forms" },
              { label: "Import CSV", path: "/grants/forms/csv-import" },
            ],
          },
        ]
      : []),
    // Dotacje (legacy KFS/BUR) - tylko jeśli ma uprawnienia grants:schedule
    // Inner submenu items inherit access from outer `hasGrantsAccess` guard — no redundant checks needed.
    ...(hasGrantsAccess
      ? [
          {
            id: "grants-kfs",
            label: "KFS (archiwum)",
            path: "/grants/kfs",
            icon: ChartBarIcon,
            // Archiwum: linki kierują do nowego, unified modułu (nabory→nabory,
            // artykuły→artykuły), filtrowanego po scheme=kfs.
            submenu: [
              { label: "Nabory", path: KFS_CALLS_HREF },
              { label: "Artykuły", path: KFS_ARTICLES_HREF },
            ],
          },
          {
            id: "grants-bur",
            label: "BUR (archiwum)",
            path: "/grants/bur",
            icon: DocumentIcon,
            // Archiwum: linki kierują do nowego, unified modułu (nabory→nabory,
            // artykuły→artykuły), filtrowanego po scheme=bur.
            submenu: [
              { label: "Nabory", path: BUR_CALLS_HREF },
              { label: "Artykuły", path: BUR_ARTICLES_HREF },
            ],
          },
        ]
      : []),
    // Nabory - single unified section for all users with any grants or admin:kiedynabory:write access
    // AC-1: replaces both grants-calls-customer and the old grants-fers (Nabory Admin) blocks
    ...(hasNaboryAccess
      ? [
          {
            id: "grants-fers",
            label: "Nabory",
            path: ACTIVE_CALLS_PATH,
            icon: ChartBarIcon,
            dataTour: "sidebar-grants",
            submenu: [
              // AC-3: Aktywne / Archiwalne / Alerty / Artykuły — admin OR grants:schedule OR grants:calls
              ...((isAdmin || hasGrantsAccess || hasGrantsCallsAccess)
                ? [
                    { label: "Aktywne", path: ACTIVE_CALLS_PATH, dataTour: "sidebar-grants-active" },
                    { label: "Archiwalne", path: "/grants/calls/archive" },
                    { label: "Artykuły", path: "/grants/articles" },
                    { label: "Alerty", path: "/grants/alerts", dataTour: "sidebar-alerts" },
                  ]
                : []),
              // AC-3: moderator items — admin OR admin:kiedynabory:write
              ...((isAdmin || hasAdminKNWrite)
                ? [
                    { label: "Programy", path: "/grants/programs" },
                    { label: "Konkursy", path: "/grants/competitions" },
                    { label: "Operatorzy", path: "/grants/operators" },
                    { label: "Projekty operatorów", path: "/grants/projects" },
                    { label: "Rekomendacje", path: "/grants/admin/recommendations" },
                    { label: "Konfiguracje przetwarzania", path: "/grants/admin/processing-configs" },
                    { label: "Źródła", path: "/grants/sources" },
                    { label: "Odkryte artykuły", path: "/grants/discovery-articles" },
                    { label: "Koszty LLM", path: "/grants/admin/llm-usage" },
                    { label: "Statystyki maili", path: "/grants/admin/email-tracking" },
                    { label: "Statystyki przeglądania", path: "/grants/admin/call-views" },
                    { label: "Generacje de minimis", path: "/grants/admin/form-generations" },
                    { label: "Błędy pipeline", path: "/grants/admin/processing-errors", badgeCount: processingErrorsSummary?.unresolvedCount },
                  ]
                : []),
            ],
          },
        ]
      : []),
    // Konto użytkownika - dostępne dla wszystkich
    {
      id: "account",
      label: "Konto",
      path: "/account",
      icon: SettingsIcon,
      submenu: [{ label: "Ustawienia konta", path: "/account/settings" }],
    },
    // Organizacja (Tenant Admin) - dla tenant_admin i globalnych adminów (AC-12)
    ...(hasModuleEnabled("tenant:users:manage")
      ? [
          {
            id: "tenant-admin",
            label: "Organizacja",
            path: "/tenant",
            icon: SettingsIcon,
            submenu: [
              { label: "Użytkownicy", path: "/tenant/users" },
              // Klucze API - dla użytkowników z uprawnieniem api-keys:manage
              ...(hasModuleEnabled("api-keys:manage")
                ? [
                    { label: "Klucze API", path: "/tenant/api-keys" },
                    { label: "Konta serwisowe", path: "/tenant/service-accounts" },
                  ]
                : []),
            ],
          },
        ]
      : []),
    // Rozliczenia section (PA-7 AC-12)
    ...(hasModuleEnabled("tenant:users:manage")
      ? [
          {
            id: "billing-section",
            label: "Rozliczenia",
            path: "/tenant/subscription",
            icon: DocumentIcon,
            dataTestId: "billing-nav-section",
            submenu: [
              { label: "Subskrypcja", path: "/tenant/subscription", dataTestId: "nav-link-subscription" },
              { label: "Dane firmowe", path: "/tenant/company", dataTestId: "billing-nav-company" },
              { label: "Zamówienia", path: "/tenant/orders", dataTestId: "billing-nav-orders" },
              { label: "Dokumenty", path: "/tenant/billing" },
            ],
          },
        ]
      : []),
    // Administracja Systemowa - tylko dla globalnych administratorów
    ...(user?.role === "admin"
      ? [
          {
            id: "system-admin",
            label: "Administracja systemowa",
            path: "/admin",
            icon: SettingsIcon,
            submenu: [
              { label: "Organizacje", path: "/admin/tenants" },
              // C1: "Organizacje (Legacy)" removed from menu in all environments.
              // The route /admin/tenants/legacy is still accessible as fallback for admins who know the URL.
              { label: "Archiwum usunięć tenantów", path: "/admin/tenant-deletions" },
              { label: "Plany subskrypcji (Legacy)", path: "/admin/subscription-plans" },
              { label: "Marki", path: "/admin/brands" },
              { label: "Billing — Produkty", path: "/admin/billing/products" },
              { label: "Billing — Katalog", path: "/admin/billing/catalog" },
              { label: "Billing — Subskrypcje", path: "/admin/billing/subscriptions" },
              { label: "Billing — Zaproszenia na okres próbny", path: "/admin/trial-invites" },
              { label: "Billing — Zamowienia", path: "/admin/billing/orders" },
              { label: "Billing — Cennik", path: "/admin/billing/pricing" },
              { label: "Rozliczenia KiedyNabory", path: "/admin/kiedynabory-settlements" },
              { label: "Proformy", path: "/admin/proforma-invoices" },
              { label: "Outbox Dashboard", path: "/admin/outbox" },
              { label: "Odzyskiwanie znaku wodnego", path: "/admin/grants/watermark-recovery" },
            ],
          },
        ]
      : []),
    // Rozliczenia KiedyNabory - dla użytkowników z modułem admin:kiedynabory (nie-adminów)
    // Supports both legacy (admin:kiedynabory) and new granular permissions
    ...(user?.role !== "admin" && (hasLegacyAdminKN || hasAdminKNRead || hasAdminKNRozliczeniaPaulina || hasAdminKNRozliczeniaTenants)
      ? [
          {
            id: "kiedynabory-admin",
            label: "KiedyNabory Admin",
            path: "/admin/kiedynabory",
            icon: ChartBarIcon,
            submenu: [
              // Rozliczenia Paulina - only for users with rozliczenia-paulina or legacy permission
              ...((hasLegacyAdminKN || hasAdminKNRozliczeniaPaulina)
                ? [{ label: "Rozliczenia Paulina", path: "/admin/kiedynabory-settlements?type=paulina" }]
                : []),
              // Rozliczenia Tenants - only for users with rozliczenia-tenants or legacy permission
              ...((hasLegacyAdminKN || hasAdminKNRozliczeniaTenants)
                ? [{ label: "Rozliczenia Tenants", path: "/admin/kiedynabory-settlements?type=tenants" }]
                : []),
              // Proformy - for users with read or legacy permission
              ...((hasLegacyAdminKN || hasAdminKNRead)
                ? [{ label: "Proformy", path: "/admin/proforma-invoices" }]
                : []),
            ],
          },
        ]
      : []),
    {
      id: "help",
      label: "Pomoc",
      path: "/help",
      icon: HelpIcon,
      dataTour: "sidebar-help",
    },
  ];

  return (
    <>
      <div
        data-tour="sidebar"
        className={`bg-white shadow-lg border-r border-neutral-200 transition-all duration-300 ease-in-out flex-col relative z-40 h-full flex-shrink-0 ${
          collapsed ? "w-16 hidden lg:flex" : "w-full lg:w-64 flex fixed lg:relative inset-y-0 left-0"
        }`}
      >
        {/* Logo/Header */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-neutral-200">
          {collapsed ? (
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-primary-600 rounded-lg flex items-center justify-center">
                <HotelIcon className="h-5 w-5 text-white" />
              </div>
            </div>
          ) : (
            <>
              <Link to="/" className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 bg-primary-600 rounded-lg flex items-center justify-center">
                    <HotelIcon className="h-5 w-5 text-white" />
                  </div>
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-neutral-900">
                    {getTenantDisplayInfo().title}
                  </h1>
                  {getTenantDisplayInfo().description && (
                    <p className="text-xs text-neutral-500">
                      {getTenantDisplayInfo().description}
                    </p>
                  )}
                </div>
              </Link>
              {/* Mobile close button */}
              <button
                onClick={onToggle}
                className="lg:hidden p-2 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
                aria-label="Zamknij menu"
              >
                <CloseIcon className="h-5 w-5" />
              </button>
            </>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {menuItems.map((item) => (
            <MenuItem
              key={item.id}
              item={item}
              collapsed={collapsed}
              isActive={isActive}
            />
          ))}
        </nav>

        {/* Toggle Button */}
        <div className="p-2 border-t border-neutral-200">
          <button
            onClick={onToggle}
            className="w-full flex items-center justify-center p-2 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
            title={collapsed ? "Rozwiń menu" : "Zwiń menu"}
          >
            {collapsed ? (
              <ChevronRightIcon className="h-5 w-5" />
            ) : (
              <>
                <ChevronLeftIcon className="h-5 w-5 mr-2" />
                <span className="text-sm">Zwiń menu</span>
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
